import type { ExerciseDef } from './types'

// ─── 4.1 Slope of a curve ─────────────────────────────────────────────────────

export const shrinkingH: ExerciseDef = {
  starter: `const f = (x: number) => x * x

// The slope between x = 3 and x = 3 + h, for smaller and smaller h:
for (const h of [1, 0.1, 0.01, 0.001, 0.0001]) {
  const slope = (f(3 + h) - f(3)) / h
  console.log("h =", h, "  slope ≈", slope)
}
// It closes in on 6. That limit is the derivative of x² at x = 3.
`,
}

export const numericalDerivative: ExerciseDef = {
  starter: `// derivative(f, x): estimate the slope of f at x.
// The "centered" formula is more accurate than (f(x+h) − f(x)) / h:
//   ( f(x + h) − f(x − h) ) / (2h)     with a small h like 0.00001
function derivative(f: (x: number) => number, x: number): number {
  const h = 1e-5
  return 0
}

console.log(derivative(x => x * x, 3))       // should be about 6
console.log(derivative(Math.sin, 0))         // should be about 1
`,
  solution: `function derivative(f: (x: number) => number, x: number): number {
  const h = 1e-5
  return (f(x + h) - f(x - h)) / (2 * h)
}

console.log(derivative(x => x * x, 3))
console.log(derivative(Math.sin, 0))
`,
  tests: `
test("slope of x² at 3 is 6", () => expect(derivative(x => x * x, 3), "derivative(x => x * x, 3)").toBeCloseTo(6, 1e-4))
test("slope of x³ at 2 is 12", () => expect(derivative(x => x ** 3, 2), "derivative(x => x ** 3, 2)").toBeCloseTo(12, 1e-4))
test("slope of a constant is 0", () => expect(derivative(() => 7, 1.5), "derivative(() => 7, 1.5)").toBeCloseTo(0, 1e-6))
test("slope of eˣ at 0 is 1", () => expect(derivative(Math.exp, 0), "derivative(Math.exp, 0)").toBeCloseTo(1, 1e-4))
`,
}

// ─── 4.2 Derivative rules ─────────────────────────────────────────────────────

export const checkRules: ExerciseDef = {
  starter: `// Check the rules numerically: does the shortcut match the zoomed-in slope?
const slope = (f: (x: number) => number, x: number) => (f(x + 1e-5) - f(x - 1e-5)) / 2e-5

const x = 1.7
console.log("x³:   numeric", slope(t => t ** 3, x).toFixed(5), "  rule 3x² =", (3 * x ** 2).toFixed(5))
console.log("eˣ:   numeric", slope(Math.exp, x).toFixed(5), "  rule eˣ  =", Math.exp(x).toFixed(5))
console.log("ln x: numeric", slope(Math.log, x).toFixed(5), "  rule 1/x =", (1 / x).toFixed(5))
console.log("5x²+3x: numeric", slope(t => 5 * t * t + 3 * t, x).toFixed(5), "  rule 10x+3 =", (10 * x + 3).toFixed(5))
`,
}

export const polyDerivative: ExerciseDef = {
  starter: `// A polynomial as a list of coefficients: [a0, a1, a2, ...] means
//   a0 + a1·x + a2·x² + a3·x³ + ...
// e.g. [1, 3, 5] is 1 + 3x + 5x²

// 1. evalPoly(c, x): compute the polynomial's value at x.
function evalPoly(c: number[], x: number): number {
  return 0
}

// 2. derivePoly(c): the derivative's coefficients, using the power rule.
//    d/dx of aₙ·xⁿ is n·aₙ·xⁿ⁻¹ — so [1, 3, 5] → [3, 10]
function derivePoly(c: number[]): number[] {
  return []
}

console.log(evalPoly([1, 3, 5], 2), derivePoly([1, 3, 5]))
`,
  solution: `function evalPoly(c: number[], x: number): number {
  let total = 0
  for (let n = 0; n < c.length; n++) total += c[n] * x ** n
  return total
}

function derivePoly(c: number[]): number[] {
  const out: number[] = []
  for (let n = 1; n < c.length; n++) out.push(n * c[n])
  return out
}

console.log(evalPoly([1, 3, 5], 2), derivePoly([1, 3, 5]))
`,
  tests: `
test("evalPoly([1, 3, 5], 2) is 27", () => expect(evalPoly([1, 3, 5], 2), "evalPoly([1, 3, 5], 2)").toBe(27))
test("evalPoly of a constant", () => expect(evalPoly([4], 10), "evalPoly([4], 10)").toBe(4))
test("derivePoly([1, 3, 5]) is [3, 10]", () => expect(derivePoly([1, 3, 5]), "derivePoly([1, 3, 5])").toEqual([3, 10]))
test("derivePoly of x³ is 3x²", () => expect(derivePoly([0, 0, 0, 1]), "derivePoly([0, 0, 0, 1])").toEqual([0, 0, 3]))
test("the derivative of a constant is nothing", () => expect(derivePoly([7]), "derivePoly([7])").toEqual([]))
test("derivative matches the numerical slope", () => {
  const c = [2, -1, 0.5, 0.25]
  const x = 1.3
  const numeric = (evalPoly(c, x + 1e-5) - evalPoly(c, x - 1e-5)) / 2e-5
  expect(evalPoly(derivePoly(c), x), "slope from derivePoly").toBeCloseTo(numeric, 1e-4)
})
`,
}

// ─── 4.3 Chain rule ───────────────────────────────────────────────────────────

export const chainCheck: ExerciseDef = {
  starter: `// y = (3x + 1)²  — a function inside a function.
// Chain rule: dy/dx = (outer slope) × (inner slope) = 2(3x + 1) × 3
const y = (x: number) => (3 * x + 1) ** 2
const chainRule = (x: number) => 2 * (3 * x + 1) * 3
const numeric = (x: number) => (y(x + 1e-5) - y(x - 1e-5)) / 2e-5

for (const x of [-1, 0, 0.5, 2]) {
  console.log("x =", x, "  chain rule:", chainRule(x), "  numeric:", numeric(x).toFixed(4))
}
`,
}

export const chainBuild: ExerciseDef = {
  starter: `// y = sin(x²) is sin applied to u = x².
//   outer: sin(u)  has slope cos(u)
//   inner: x²      has slope 2x
// 1. Write dSinSquare(x) using the chain rule.
function dSinSquare(x: number): number {
  return 0
}

// 2. Sigmoid σ(z) = 1 / (1 + e^(−z)) has a famous slope: σ(z)·(1 − σ(z)).
//    Write dSigmoidOfLine(x, w, b): the slope of σ(w·x + b) with respect to x.
//    (Chain rule: outer slope σ'(z) at z = w·x + b, times the inner slope w.)
const sigmoid = (z: number) => 1 / (1 + Math.exp(-z))
function dSigmoidOfLine(x: number, w: number, b: number): number {
  return 0
}

console.log(dSinSquare(1), dSigmoidOfLine(0.5, 2, -1))
`,
  solution: `function dSinSquare(x: number): number {
  return Math.cos(x * x) * 2 * x
}

const sigmoid = (z: number) => 1 / (1 + Math.exp(-z))
function dSigmoidOfLine(x: number, w: number, b: number): number {
  const s = sigmoid(w * x + b)
  return s * (1 - s) * w
}

console.log(dSinSquare(1), dSigmoidOfLine(0.5, 2, -1))
`,
  tests: `
const __num = (f, x) => (f(x + 1e-5) - f(x - 1e-5)) / 2e-5
test("dSinSquare matches at x = 1", () => expect(dSinSquare(1), "dSinSquare(1)").toBeCloseTo(__num(t => Math.sin(t * t), 1), 1e-5))
test("dSinSquare matches at x = −2.2", () => expect(dSinSquare(-2.2), "dSinSquare(-2.2)").toBeCloseTo(__num(t => Math.sin(t * t), -2.2), 1e-5))
test("dSigmoidOfLine at x=0.5, w=2, b=−1 is 0.5", () => expect(dSigmoidOfLine(0.5, 2, -1), "dSigmoidOfLine(0.5, 2, -1)").toBeCloseTo(0.5, 1e-6))
test("dSigmoidOfLine matches a numerical slope", () => {
  const s = z => 1 / (1 + Math.exp(-z))
  expect(dSigmoidOfLine(1.3, -0.7, 0.4), "dSigmoidOfLine(1.3, -0.7, 0.4)").toBeCloseTo(__num(x => s(-0.7 * x + 0.4), 1.3), 1e-6)
})
`,
}

// ─── 4.4 Gradients & descent ─────────────────────────────────────────────────

export const partials: ExerciseDef = {
  starter: `// f(x, y) = x² + 3y  — two inputs, so two slopes:
const f = (x: number, y: number) => x * x + 3 * y
const h = 1e-5

const x = 2, y = 1
const dfdx = (f(x + h, y) - f(x - h, y)) / (2 * h)   // wiggle x, hold y still
const dfdy = (f(x, y + h) - f(x, y - h)) / (2 * h)   // wiggle y, hold x still
console.log("gradient at (2, 1):", [dfdx, dfdy])    // ≈ [4, 3]
`,
}

export const descend: ExerciseDef = {
  starter: `// Find the lowest point of f(x, y) = (x − 3)² + (y + 1)² using gradient descent.
// Its gradient is [2(x − 3), 2(y + 1)].
function gradient(x: number, y: number): [number, number] {
  return [2 * (x - 3), 2 * (y + 1)]
}

// minimize(): start at (0, 0). Repeat 100 times:
//   x ← x − lr · ∂f/∂x
//   y ← y − lr · ∂f/∂y
// Return the final [x, y].
function minimize(lr: number): [number, number] {
  let x = 0
  let y = 0
  // your loop here
  return [x, y]
}

console.log(minimize(0.1))   // should land very near [3, -1]
`,
  solution: `function gradient(x: number, y: number): [number, number] {
  return [2 * (x - 3), 2 * (y + 1)]
}

function minimize(lr: number): [number, number] {
  let x = 0
  let y = 0
  for (let step = 0; step < 100; step++) {
    const [gx, gy] = gradient(x, y)
    x = x - lr * gx
    y = y - lr * gy
  }
  return [x, y]
}

console.log(minimize(0.1))
`,
  tests: `
test("minimize(0.1) reaches about (3, −1)", () => expect(minimize(0.1), "minimize(0.1)").toBeCloseTo([3, -1], 1e-3))
test("a smaller learning rate still gets close", () => expect(minimize(0.05), "minimize(0.05)").toBeCloseTo([3, -1], 0.01))
test("a learning rate that's too big blows up", () => expect(Math.abs(minimize(1.1)[0]), "|x| with lr = 1.1").toBeGreaterThan(1000))
`,
}

export const descendPlot: ExerciseDef = {
  starter: `// Watch the loss fall as gradient descent runs.
const f = (x: number, y: number) => (x - 3) ** 2 + (y + 1) ** 2
let x = 0, y = 0
const lr = 0.08
const losses: number[] = []

for (let step = 0; step < 50; step++) {
  losses.push(f(x, y))
  const gx = 2 * (x - 3)
  const gy = 2 * (y + 1)
  x -= lr * gx
  y -= lr * gy
}
plot.line(losses, { title: "loss while descending", xLabel: "step" })
console.log("ended at", [round(x, 4), round(y, 4)])
`,
}
